import { useEffect, useRef } from 'react';

export type ProjectUpdateEvent = {
    percent?: number;
    status?: string;
    score_data?: any;
    error?: string;
};

export const useMultiProjectUpdates = (projectIds: string[], onUpdate: (projectId: string, data: ProjectUpdateEvent) => void) => {
    const eventSourcesRef = useRef<Record<string, EventSource>>({});

    useEffect(() => {
        if (!projectIds || projectIds.length === 0) {
            // Clean up old event sources if projectIds becomes empty
            Object.values(eventSourcesRef.current).forEach(es => es.close());
            eventSourcesRef.current = {};
            return;
        }

        const currentEventSources = { ...eventSourcesRef.current };
        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

        // Close connections for projects that are no longer in the list
        Object.keys(currentEventSources).forEach(projectId => {
            if (!projectIds.includes(projectId)) {
                currentEventSources[projectId].close();
                delete currentEventSources[projectId];
            }
        });

        // Create new connections for new projects
        projectIds.forEach(projectId => {
            if (!currentEventSources[projectId]) {
                const url = `${apiUrl}/api/v1/sse/project/${projectId}`;
                const eventSource = new EventSource(url, { withCredentials: true });

                eventSource.onopen = () => {
                    console.log(`SSE connection opened for project ${projectId}`);
                };

                eventSource.addEventListener("update", (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        console.log(`SSE Update for ${projectId}:`, data);
                        onUpdate(projectId, data);
                    } catch (e) {
                        console.error(`Error parsing SSE data for project ${projectId}`, e);
                    }
                });

                eventSource.onerror = (err) => {
                    console.error(`SSE connection error for project ${projectId}:`, err);
                    // EventSource will retry automatically. We can close it here if we want to handle it differently.
                    eventSource.close();
                    delete currentEventSources[projectId];
                };

                currentEventSources[projectId] = eventSource;
            }
        });

        eventSourcesRef.current = currentEventSources;

        // Cleanup function
        return () => {
            // This will be called when the component unmounts
            Object.values(eventSourcesRef.current).forEach(es => es.close());
            eventSourcesRef.current = {};
        };
    }, [projectIds, onUpdate]);
};
